import { SelectChip } from '@lastbrand/web-ui/select-chip'
import { cva } from '@lastbrand/web-ui-styled-system/css'
import React from 'react'

// LinkPure, not Link: the styled `Link` atom's allow-list drops `aria-current` (and any other
// unknown DOM prop), so the active chip would announce no current-page state in production —
// LinkPure types and forwards it on both its anchor branches (Copilot, PR #7895 round 3).
import { LinkPure } from '@src/quince-components/src/atoms/link'

import { SiblingProductOptionType } from './sibling-product-options-ds'

// LINK chips CANNOT go through `SelectChip asChild`: the shared `Link` atom destructures a fixed
// prop allow-list and never spreads rest, so Radix Slot's injected `data-state`, `role`,
// `aria-checked` and keyboard handlers are silently dropped on the real anchor (the test mocks
// spread `...rest`, which is why tests alone can't catch it — Copilot review on PR #7895).
// Until `Link` forwards unknown DOM props, the LINK variant renders a plain anchor styled by this
// verbatim copy of the web-ui `select-chip` recipe (node_modules/@lastbrand/web-ui/dist/
// select-chip.js, `variant: rectangle`), with the selected state driven by `isActive` instead of
// `data-state="on"`. Keep in sync with the library recipe when web-ui bumps.
const linkChipRecipe = cva({
  base: {
    paddingX: 'space-16',
    paddingY: 'space-10',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 'min-w.chip',
    bg: 'white',
    boxShadow: 'inset 0 0 0 1px {colors.lightgray}',
    color: 'black',
    textStyle: 'body-14',
    transition: 'background-color 150ms ease-out, box-shadow 150ms ease-out, color 150ms ease-out',
    cursor: 'pointer',
    userSelect: 'none',
    borderRadius: 'none',
    _focusVisible: {
      outlineWidth: '2px',
      outlineOffset: '0.25rem',
      outlineColor: 'black',
      outlineStyle: 'solid',
    },
    // Gate hover/active to real-hover pointers — a plain anchor has no ToggleGroup press-state
    // to recover from a sticky emulated :hover (STRFRNT-16900 bug class); the web-ui select-chip
    // recipe ships the same guard.
    '@media (hover: hover)': {
      _hover: { bg: 'paperwhite', boxShadow: 'inset 0 0 0 1px {colors.lightgray}' },
      _active: { bg: 'offwhite', boxShadow: 'inset 0 0 0 1px {colors.lightgray}' },
    },
  },
  variants: {
    selected: {
      true: {
        bg: 'black',
        boxShadow: 'inset 0 0 0 1px {colors.black}',
        color: 'white',
        textStyle: 'body-14-strong',
        '@media (hover: hover)': {
          _hover: { bg: 'gray', boxShadow: 'inset 0 0 0 1px {colors.black}' },
          _active: { bg: 'mediumgray', boxShadow: 'inset 0 0 0 1px {colors.black}' },
        },
      },
    },
  },
})

interface SiblingProductButtonDSProps {
  value: string
  displayName: string
  isActive: boolean
  variant: SiblingProductOptionType
  /** Radix ToggleGroup item identity — the string the parent `SelectChipGroup`'s
   * single `value` compares against, so the BUTTON chip lights up. */
  selectionValue: string
  onClickCallback?: (val: string) => void
  // Accepted for API parity with legacy callers; DS callers pass undefined and the
  // chip itself no longer takes a className (styling comes from the SelectChip recipe).
  className?: string
  id?: string
  testId?: string
}

// STRFRNT-16944 follow-up: BUTTON chips render the shared web-ui `SelectChip` atom (the same one
// TextChipGroupDS uses); LINK chips render a recipe-parity anchor (see linkChipRecipe above).
function SiblingProductButtonDS({
  variant,
  value,
  displayName,
  isActive,
  selectionValue,
  onClickCallback,
  id,
  testId,
}: SiblingProductButtonDSProps) {
  return variant === SiblingProductOptionType.LINK ? (
    <LinkPure
      id={id}
      // Only emit the prop when set — an explicit `data-testid: undefined` would override the
      // Link atom's own id-derived testid (and test doubles that spread rest).
      {...((testId ?? id) ? { 'data-testid': testId ?? id } : {})}
      href={value}
      onClick={onClickCallback}
      // eslint-disable-next-line i18next/no-literal-string -- ARIA token, not user copy
      aria-current={isActive ? 'page' : undefined}
      className={linkChipRecipe({ selected: isActive })}
    >
      {displayName || value}
    </LinkPure>
  ) : (
    <SelectChip id={id} data-testid={testId ?? id} value={selectionValue} variant="rectangle">
      {displayName || value}
    </SelectChip>
  )
}

export default SiblingProductButtonDS
